import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@atrvd/saas-ui'
import { AlertTriangle } from 'lucide-react'
import {
  describeSyncFailures,
  type ConnectionSyncFailure,
} from '@/lib/ledgersync-sync-activity'

/** Props for {@link SyncFailureIndicator}. */
interface SyncFailureIndicatorProps {
  /** Failed pipelines of one connection; renders nothing when empty. */
  failures: readonly ConnectionSyncFailure[]
}

/**
 * Small warning glyph beside a connection's sync date when its latest sync
 * ended `failed` (DCP-401), with the raw reason on hover/focus.
 *
 * Deliberately unobtrusive — the same Tooltip-over-a-trigger pattern the
 * list's Status badge uses for `last_error`. A failed SYNC is not a broken
 * CONNECTION (the next scheduled run may well succeed), so it gets a hint next
 * to the date rather than a second status badge.
 *
 * Focusable, with the message as its accessible name, so keyboard and
 * screen-reader users reach the reason without hovering.
 */
export function SyncFailureIndicator({ failures }: SyncFailureIndicatorProps) {
  if (failures.length === 0) return null
  const description = describeSyncFailures(failures)
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          role="img"
          aria-label={description}
          className="inline-flex cursor-help text-destructive"
        >
          <AlertTriangle aria-hidden="true" className="h-3.5 w-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipPortal>
        <TooltipContent className="max-w-xs whitespace-pre-line">{description}</TooltipContent>
      </TooltipPortal>
    </Tooltip>
  )
}
